import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, inr } from "../lib/api";
import Shell, { StoreBadge } from "../components/Shell";
import ApprovalModal from "../components/ApprovalModal";
import { useAuth } from "../lib/auth";
import { hasPerm } from "../components/Sidebar";
import { computePosTotals, apiErrorMessage, apiErrorCode } from "../lib/pricing";
import { MagnifyingGlass, Trash, Barcode, Plus, Minus, CurrencyInr, User, Tag, Wallet, Receipt, Lock, ShieldCheck } from "@phosphor-icons/react";

const PAYMENT_METHODS = ["cash", "upi", "card", "other", "pending", "credit_note"];

export default function POS() {
  const { storeId } = useParams();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [lines, setLines] = useState([]);
  const [customer, setCustomer] = useState({ name: "Walk-in", phone: "", gstin: "", state_code: "", address: "" });
  const [customerInfo, setCustomerInfo] = useState(null); // returning customer stats
  const [shipToDifferent, setShipToDifferent] = useState(false);
  const [shipTo, setShipTo] = useState({ name: "", phone: "", address: "", gstin: "", state_code: "" });
  const [discountMode, setDiscountMode] = useState("flat"); // flat | percent
  const [discountValue, setDiscountValue] = useState(0);
  const [roundOff, setRoundOff] = useState(true);
  const [payments, setPayments] = useState([{ method: "cash", amount: 0, reference: "" }]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [executives, setExecutives] = useState([]);
  const [executive, setExecutive] = useState("");
  const [discountReason, setDiscountReason] = useState("");
  const [policy, setPolicy] = useState(null);
  const [approval, setApproval] = useState(null); // { approval_token, approver, expires_at }
  const [showApproval, setShowApproval] = useState(false);
  const [approvalReason, setApprovalReason] = useState("");
  const { user } = useAuth();
  const canOverridePrice = hasPerm(user, "pos.price_override") || approval;
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    api.get("/executives").then((r) => setExecutives(r.data || [])).catch(() => {});
    api.get("/settings/discount-policy").then((r) => setPolicy(r.data)).catch(() => {});
  }, []);
  // Approval tokens are single-use + 5 min; drop it from the UI once expired
  useEffect(() => {
    if (!approval) return;
    const ms = new Date(approval.expires_at).getTime() - Date.now();
    const t = setTimeout(() => setApproval(null), Math.max(0, ms));
    return () => clearTimeout(t);
  }, [approval]);

  // Customer auto-lookup on phone
  useEffect(() => {
    const ph = (customer.phone || "").trim();
    if (ph.length < 10) { setCustomerInfo(null); return; }
    const t = setTimeout(() => {
      api.get(`/customers/${encodeURIComponent(ph)}`)
        .then((r) => {
          setCustomerInfo(r.data);
          setCustomer((c) => ({
            ...c,
            name: c.name === "Walk-in" || !c.name ? r.data.name || c.name : c.name,
            gstin: c.gstin || r.data.gstin || "",
            state_code: c.state_code || r.data.state_code || "",
          }));
        })
        .catch(() => setCustomerInfo(null));
    }, 350);
    return () => clearTimeout(t);
  }, [customer.phone]);

  const search = async (code) => {
    const qs = code || q;
    if (!qs) return;
    try {
      const { data } = await api.get(`/store/${storeId}/inventory/lookup?code=${encodeURIComponent(qs)}`);
      addLine(data);
      setQ("");
      setResults([]);
      return;
    } catch (e) { /* fall through to text search */ }
    const { data } = await api.get(`/store/${storeId}/inventory?q=${encodeURIComponent(qs)}`);
    setResults(data.slice(0, 20));
  };

  const addLine = (p) => {
    const exist = lines.findIndex((l) => l.sku === p.sku);
    if (exist >= 0) {
      const copy = [...lines];
      copy[exist].qty += 1;
      setLines(copy);
    } else {
      setLines([...lines, {
        sku: p.sku, product_name: p.product_name, barcode: p.barcode || "", hsn: p.hsn || "",
        gst_rate: p.gst_rate || 0, qty: 1, rate: p.selling_price || 0,
        discount: 0, discount_pct: 0, discount_mode: "flat",
      }]);
    }
  };
  const updateLine = (i, key, val) => {
    const copy = [...lines]; copy[i] = { ...copy[i], [key]: val }; setLines(copy);
  };
  const updateQty = (i, delta) => { const c = [...lines]; c[i].qty = Math.max(1, c[i].qty + delta); setLines(c); };
  const remove = (i) => setLines(lines.filter((_, idx) => idx !== i));

  // Compute totals — same engine as the backend (discounts before tax, shared bill discount)
  const totals = useMemo(() => computePosTotals(lines, discountMode, discountValue, roundOff), [lines, discountMode, discountValue, roundOff]);
  const myCap = approval ? (policy?.limits?.[approval.approver.role] ?? 100) : (policy?.my_limit_pct ?? 100);
  const overCap = totals.givenPct > myCap + 1e-9;
  const needsReason = (policy?.require_reason ?? true) && totals.totalDiscount > 0;

  // Keep first payment auto-balanced if user hasn't customized
  useEffect(() => {
    if (payments.length === 1) {
      setPayments([{ ...payments[0], amount: totals.grand }]);
    }
  }, [totals.grand]);

  const paymentSum = useMemo(() => payments.reduce((a, p) => a + (parseFloat(p.amount) || 0), 0), [payments]);
  const paymentDelta = +(totals.grand - paymentSum).toFixed(2);

  // Credit note verification per payment row
  const [cnInfo, setCnInfo] = useState({});
  const verifyCN = async (i) => {
    const ref = (payments[i].reference || "").trim();
    if (!ref) { setCnInfo((m) => ({ ...m, [i]: { status: "err", msg: "Enter the CN number first" } })); return; }
    try {
      const r = await api.get(`/store/${storeId}/credit-notes/lookup?cn_no=${encodeURIComponent(ref)}`);
      const d = r.data;
      if (d.refund_method !== "store_credit") {
        setCnInfo((m) => ({ ...m, [i]: { status: "err", msg: `${d.cn_no} was refunded via ${(d.refund_method || "other").toUpperCase()} — not redeemable` } }));
        return;
      }
      if (d.balance <= 0) {
        setCnInfo((m) => ({ ...m, [i]: { status: "err", msg: `${d.cn_no} is already settled — no balance left` } }));
        return;
      }
      const current = parseFloat(payments[i].amount) || 0;
      const suggested = Math.min(d.balance, Math.max(0, +(paymentDelta + current).toFixed(2)));
      const c = [...payments];
      c[i] = { ...c[i], reference: d.cn_no, amount: suggested > 0 ? suggested : c[i].amount };
      setPayments(c);
      setCnInfo((m) => ({ ...m, [i]: { status: "ok", msg: `${d.cn_no} · ${d.customer_name || "Walk-in"} · balance ${inr(d.balance)}` } }));
    } catch (e) {
      setCnInfo((m) => ({ ...m, [i]: { status: "err", msg: e.response?.data?.detail || e.message } }));
    }
  };

  const updatePayment = (i, key, val) => {
    const c = [...payments]; c[i] = { ...c[i], [key]: val }; setPayments(c);
    if ((key === "method" || key === "reference") && cnInfo[i]) {
      setCnInfo((m) => { const n = { ...m }; delete n[i]; return n; });
    }
  };
  const addPayment = () => {
    const used = new Set(payments.map((p) => p.method));
    const next = PAYMENT_METHODS.find((m) => !used.has(m)) || "other";
    setPayments([...payments, { method: next, amount: Math.max(0, paymentDelta), reference: "" }]);
  };
  const removePayment = (i) => { setPayments(payments.filter((_, idx) => idx !== i)); setCnInfo({}); };
  const autoBalanceLast = () => {
    if (payments.length === 0) return;
    const others = payments.slice(0, -1).reduce((a, p) => a + (parseFloat(p.amount) || 0), 0);
    const last = +(totals.grand - others).toFixed(2);
    const c = [...payments]; c[c.length - 1] = { ...c[c.length - 1], amount: Math.max(0, last) }; setPayments(c);
  };

  const checkout = async () => {
    if (lines.length === 0) return;
    if (!executive) {
      alert("Please select the Executive handling this bill (required).");
      return;
    }
    if (Math.abs(paymentDelta) > 0.05) {
      alert(`Payment mismatch: ${inr(paymentDelta)} remaining. Adjust payment amounts.`);
      return;
    }
    if (overCap && !approval) { setApprovalReason(`Discount ${totals.givenPct}% exceeds your ${myCap}% limit.`); setShowApproval(true); return; }
    if (needsReason && !discountReason.trim()) { alert("Please enter a reason for the discount."); return; }
    setSaving(true);
    try {
      const body = {
        store_id: storeId,
        executive_name: executive,
        customer_name: customer.name,
        customer_phone: customer.phone,
        customer_gstin: customer.gstin,
        customer_state_code: customer.state_code,
        customer_address: customer.address || "",
        ship_to_different: shipToDifferent,
        ship_to_name: shipToDifferent ? shipTo.name : "",
        ship_to_phone: shipToDifferent ? shipTo.phone : "",
        ship_to_address: shipToDifferent ? shipTo.address : "",
        ship_to_gstin: shipToDifferent ? shipTo.gstin : "",
        ship_to_state_code: shipToDifferent ? shipTo.state_code : "",
        items: lines.map((l) => ({
          sku: l.sku, product_name: l.product_name, barcode: l.barcode || "",
          hsn: l.hsn || "", gst_rate: parseFloat(l.gst_rate) || 0,
          qty: parseInt(l.qty) || 1, rate: parseFloat(l.rate) || 0,
          discount: parseFloat(l.discount) || 0,
          discount_pct: parseFloat(l.discount_pct) || 0,
        })),
        payments: payments.map((p) => ({
          method: p.method, amount: parseFloat(p.amount) || 0, reference: p.reference || "",
        })),
        discount_total: discountMode === "flat" ? (parseFloat(discountValue) || 0) : 0,
        discount_pct: discountMode === "percent" ? (parseFloat(discountValue) || 0) : 0,
        round_off_enabled: roundOff,
        notes: notes.trim(),
        discount_reason: discountReason.trim(),
        approval_token: approval?.approval_token || "",
      };
      const { data } = await api.post("/store/sales", body);
      // Open PDF
      const token = localStorage.getItem("pos_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store/sales/${data.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
      // Reset
      setLines([]);
      setCustomer({ name: "Walk-in", phone: "", gstin: "", state_code: "", address: "" });
      setCustomerInfo(null);
      setShipToDifferent(false);
      setShipTo({ name: "", phone: "", address: "", gstin: "", state_code: "" });
      setDiscountValue(0); setPayments([{ method: "cash", amount: 0, reference: "" }]); setCnInfo({});
      setDiscountReason(""); setApproval(null);
      inputRef.current?.focus();
    } catch (e) {
      const code = apiErrorCode(e);
      if (code === "DISCOUNT_LIMIT" || code === "PRICE_LOCKED") {
        setApproval(null); setApprovalReason(apiErrorMessage(e)); setShowApproval(true);
      } else {
        alert("Failed: " + apiErrorMessage(e));
      }
    } finally { setSaving(false); }
  };

  return (
    <Shell>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_600px] xl:grid-cols-[1fr_720px] 2xl:grid-cols-[1fr_800px] h-screen" data-testid="pos-screen">
        {/* Left: Products */}
        <div className="overflow-y-auto bg-slate-50">
          <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-5 z-10">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-2xl font-bold">POS Billing</h1>
              <StoreBadge />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); search(); }} className="mt-4 relative">
              <Barcode size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={inputRef}
                data-testid="pos-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Scan barcode or search SKU / product…"
                className="w-full h-14 pl-10 pr-4 border border-slate-300 bg-white rounded-md text-base focus:outline-none focus:ring-2 focus:ring-emerald-500 font-medium"
              />
            </form>
          </div>

          <div className="p-6">
            {results.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {results.map((p) => (
                  <button key={p.sku} onClick={() => addLine(p)} data-testid={`pos-product-${p.sku}`} className="text-left bg-white border border-slate-200 rounded-md p-3 hover:border-emerald-500 hover:shadow transition-all">
                    <div className="flex items-start gap-3">
                      {p.thumbnail ? <img src={p.thumbnail} alt="" className="w-14 h-14 rounded-sm object-cover" /> : <div className="w-14 h-14 rounded-sm bg-slate-100" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold leading-tight line-clamp-2">{p.product_name}</div>
                        <div className="text-[10px] text-slate-500 font-mono-num mt-1">{p.sku}</div>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-emerald-700 font-bold font-mono-num">{inr(p.selling_price)}</span>
                          <span className={`text-[10px] font-mono-num ${p.qty <= 0 ? "text-red-600" : p.qty <= 5 ? "text-amber-600" : "text-slate-500"}`}>Stk {p.qty}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-24 text-slate-400">
                <Barcode size={40} weight="thin" className="mx-auto mb-3" />
                <div className="text-sm">Scan a barcode or search for a product to begin billing.</div>
              </div>
            )}
          </div>
        </div>

        {/* Right: Cart */}
        <div className="bg-white border-l border-slate-200 flex flex-col h-screen">
          <div className="px-5 py-3 border-b border-slate-200">
            <div className="font-display text-lg font-bold">Current Bill</div>
            <div className="text-xs text-slate-500">{lines.length} items</div>
          </div>

          {/* Customer */}
          <div className="p-4 border-b border-slate-200 bg-slate-50 grid grid-cols-2 gap-2">
            <input data-testid="cust-name" placeholder="Customer name" value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="col-span-2 h-9 px-2.5 border border-slate-300 rounded-md text-xs" />
            <input data-testid="cust-phone" placeholder="Phone (10-digit)" value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="h-9 px-2.5 border border-slate-300 rounded-md text-xs font-mono-num" />
            <input placeholder="State code" value={customer.state_code} onChange={(e) => setCustomer({ ...customer, state_code: e.target.value })} className="h-9 px-2.5 border border-slate-300 rounded-md text-xs font-mono-num" />
            <input placeholder="GSTIN (optional)" value={customer.gstin} onChange={(e) => {
              const g = e.target.value.toUpperCase().trim();
              // GSTIN's first 2 digits are the buyer's state — auto-fill it
              const st = g.length >= 2 && /^\d\d/.test(g) ? g.slice(0, 2) : customer.state_code;
              setCustomer({ ...customer, gstin: g, state_code: st });
            }} className="col-span-2 h-9 px-2.5 border border-slate-300 rounded-md text-xs font-mono-num" />
            <textarea
              data-testid="cust-address"
              placeholder="Billing address (optional)"
              value={customer.address}
              onChange={(e) => setCustomer({ ...customer, address: e.target.value })}
              rows={2}
              className="col-span-2 px-2.5 py-1.5 border border-slate-300 rounded-md text-xs resize-none"
            />
            <label className="col-span-2 inline-flex items-center gap-2 text-[11px] cursor-pointer mt-1">
              <input
                type="checkbox"
                checked={shipToDifferent}
                onChange={(e) => setShipToDifferent(e.target.checked)}
                data-testid="shipto-toggle"
                className="w-3.5 h-3.5"
              />
              <span className="font-semibold text-slate-700">Ship to a different address</span>
            </label>
            {shipToDifferent && (
              <div className="col-span-2 grid grid-cols-2 gap-2 p-2.5 border border-emerald-200 bg-emerald-50/50 rounded-md" data-testid="shipto-panel">
                <div className="col-span-2 text-[9px] uppercase tracking-widest font-bold text-emerald-700">Ship To (delivery address)</div>
                <input placeholder="Recipient name" value={shipTo.name} onChange={(e) => setShipTo({ ...shipTo, name: e.target.value })} data-testid="shipto-name" className="col-span-2 h-9 px-2.5 border border-slate-300 rounded-md text-xs" />
                <input placeholder="Phone" value={shipTo.phone} onChange={(e) => setShipTo({ ...shipTo, phone: e.target.value })} data-testid="shipto-phone" className="h-9 px-2.5 border border-slate-300 rounded-md text-xs font-mono-num" />
                <input placeholder="State code" value={shipTo.state_code} onChange={(e) => setShipTo({ ...shipTo, state_code: e.target.value })} data-testid="shipto-state" className="h-9 px-2.5 border border-slate-300 rounded-md text-xs font-mono-num" />
                <input placeholder="GSTIN (optional)" value={shipTo.gstin} onChange={(e) => setShipTo({ ...shipTo, gstin: e.target.value })} data-testid="shipto-gstin" className="col-span-2 h-9 px-2.5 border border-slate-300 rounded-md text-xs font-mono-num" />
                <textarea
                  placeholder="Shipping address"
                  value={shipTo.address}
                  onChange={(e) => setShipTo({ ...shipTo, address: e.target.value })}
                  rows={2}
                  data-testid="shipto-address"
                  className="col-span-2 px-2.5 py-1.5 border border-slate-300 rounded-md text-xs resize-none"
                />
                <div className="col-span-2 text-[10px] text-slate-500">
                  Ship-To address will appear as a separate block on the printed invoice.
                </div>
              </div>
            )}
            {/* Executive (internal, mandatory) */}
            <div className="col-span-2">
              <select
                value={executive}
                onChange={(e) => setExecutive(e.target.value)}
                data-testid="pos-executive"
                className={`w-full h-9 px-2.5 border rounded-md text-xs font-semibold ${executive ? "border-slate-300" : "border-amber-400 bg-amber-50/40"}`}
              >
                <option value="">Executive * (required — internal only)</option>
                {executives.map((ex) => <option key={ex.id} value={ex.name}>{ex.name}</option>)}
              </select>
              {executives.length === 0 && (
                <div className="text-[10px] text-amber-700 mt-0.5">No executives configured — ask an admin to add them in Company Settings.</div>
              )}
            </div>
            {customerInfo && (
              <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5 flex items-center gap-2 text-[11px]" data-testid="customer-banner">
                <User size={14} className="text-emerald-700" />
                <div className="flex-1">
                  <span className="font-bold text-emerald-700">Returning customer</span>
                  <span className="text-emerald-600 font-mono-num"> · {customerInfo.total_bills || 0} bills · {inr(customerInfo.total_spent || 0)} lifetime</span>
                </div>
              </div>
            )}
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto">
            {lines.length === 0 && <div className="text-center text-slate-400 p-10 text-sm">Cart is empty</div>}
            {lines.map((l, i) => (
              <div key={i} className="px-4 py-3 border-b border-slate-100" data-testid={`cart-line-${i}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold truncate">{l.product_name}</div>
                    <div className="text-[10px] text-slate-500 font-mono-num">{l.sku} · GST {l.gst_rate}%</div>
                  </div>
                  <button onClick={() => remove(i)} className="p-1 text-red-600 hover:bg-red-50 rounded-md"><Trash size={14} /></button>
                </div>
                <div className="flex items-center justify-between mt-2 gap-2">
                  <div className="inline-flex items-center border border-slate-300 rounded-md overflow-hidden">
                    <button onClick={() => updateQty(i, -1)} data-testid={`cart-qty-minus-${i}`} className="w-7 h-7 hover:bg-slate-100 flex items-center justify-center"><Minus size={12} /></button>
                    <span data-testid={`cart-qty-${i}`} className="w-8 text-center text-sm font-mono-num font-semibold">{l.qty}</span>
                    <button onClick={() => updateQty(i, 1)} data-testid={`cart-qty-plus-${i}`} className="w-7 h-7 hover:bg-slate-100 flex items-center justify-center"><Plus size={12} /></button>
                  </div>
                  <div className="relative" title={canOverridePrice ? "Selling price (you may change it)" : "Selling price is locked — use Discount, or get manager approval"}>
                    <input type="number" step="0.01" value={l.rate} readOnly={!canOverridePrice} onChange={(e) => updateLine(i, "rate", parseFloat(e.target.value) || 0)} data-testid={`cart-rate-${i}`} className={`w-24 h-7 px-2 border rounded-md text-xs font-mono-num text-right ${canOverridePrice ? "border-slate-300" : "border-slate-200 bg-slate-100 text-slate-600 pr-6 cursor-not-allowed"}`} />
                    {!canOverridePrice && <Lock size={10} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400" data-testid={`cart-rate-lock-${i}`} />}
                  </div>
                  <div className="font-mono-num font-bold text-sm text-slate-900 w-24 text-right">{inr(totals.lineCalc[i]?.total || 0)}</div>
                </div>
                {/* Per-line discount toggle */}
                <div className="flex items-center gap-2 mt-2">
                  <Tag size={10} className="text-slate-400" />
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Discount</span>
                  <div className="inline-flex border border-slate-300 rounded-md overflow-hidden text-[10px]">
                    <button onClick={() => updateLine(i, "discount_mode", "flat")} className={`px-1.5 py-0.5 ${(l.discount_mode || "flat") === "flat" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>₹</button>
                    <button onClick={() => updateLine(i, "discount_mode", "percent")} className={`px-1.5 py-0.5 ${l.discount_mode === "percent" ? "bg-slate-900 text-white" : "bg-white text-slate-600"}`}>%</button>
                  </div>
                  {(l.discount_mode || "flat") === "flat" ? (
                    <input type="number" step="0.01" value={l.discount || 0} onChange={(e) => updateLine(i, "discount", parseFloat(e.target.value) || 0)} placeholder="0" className="flex-1 h-6 px-2 border border-slate-300 rounded-md text-[10px] font-mono-num text-right" />
                  ) : (
                    <input type="number" step="0.1" value={l.discount_pct || 0} onChange={(e) => updateLine(i, "discount_pct", parseFloat(e.target.value) || 0)} placeholder="0" className="flex-1 h-6 px-2 border border-slate-300 rounded-md text-[10px] font-mono-num text-right" />
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Totals + Discount + Round-off + Payments */}
          <div className="border-t border-slate-200 p-3 space-y-1.5 bg-slate-50 text-xs">
            <div className="flex justify-between"><span className="text-slate-500">Gross (MRP × qty)</span><span className="font-mono-num" data-testid="pos-gross">{inr(totals.gross)}</span></div>
            {totals.lineDisc > 0 && <div className="flex justify-between"><span className="text-slate-500">Item discounts</span><span className="font-mono-num text-amber-700">- {inr(totals.lineDisc)}</span></div>}

            <div className="flex items-center justify-between gap-1.5 pt-1">
              <span className="text-slate-500">Bill Discount</span>
              <div className="inline-flex border border-slate-300 rounded-md overflow-hidden">
                <button onClick={() => setDiscountMode("flat")} className={`px-2 h-7 text-[10px] ${discountMode === "flat" ? "bg-slate-900 text-white" : "bg-white"}`}>₹</button>
                <button onClick={() => setDiscountMode("percent")} className={`px-2 h-7 text-[10px] ${discountMode === "percent" ? "bg-slate-900 text-white" : "bg-white"}`}>%</button>
              </div>
              <input data-testid="bill-discount" type="number" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="w-20 h-7 px-2 border border-slate-300 rounded-md text-[11px] font-mono-num text-right" />
              <span className="text-slate-500 w-20 text-right font-mono-num">- {inr(totals.billDisc)}</span>
            </div>
            <div className="flex justify-between"><span className="text-slate-500">Taxable value</span><span className="font-mono-num">{inr(totals.sub)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">GST (on discounted value)</span><span className="font-mono-num">{inr(totals.tax)}</span></div>

            {/* Discount guardrail meter */}
            {policy && (
              <div className={`rounded-md border px-2.5 py-2 ${overCap ? "bg-red-50 border-red-200" : totals.totalDiscount > 0 ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`} data-testid="discount-meter">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest font-semibold text-slate-600 inline-flex items-center gap-1"><ShieldCheck size={11} /> Discount {totals.givenPct}% · limit {myCap}%</span>
                  {approval
                    ? <span className="text-[10px] font-bold text-emerald-700" data-testid="approval-chip">Approved by {approval.approver.name || approval.approver.email}</span>
                    : overCap && <button onClick={() => { setApprovalReason(`Discount ${totals.givenPct}% exceeds your ${myCap}% limit.`); setShowApproval(true); }} data-testid="get-approval-btn" className="text-[10px] font-bold text-red-700 hover:underline">Get manager approval →</button>}
                </div>
                <div className="mt-1 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className={`h-full ${overCap ? "bg-red-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, myCap ? totals.givenPct / myCap * 100 : 100)}%` }} /></div>
                {needsReason && (
                  <input value={discountReason} onChange={(e) => setDiscountReason(e.target.value)} placeholder="Reason for discount (required) — e.g. festival offer, damaged box" data-testid="discount-reason" className={`mt-1.5 w-full h-7 px-2 border rounded-md text-[11px] ${discountReason.trim() ? "border-slate-300" : "border-amber-400"}`} />
                )}
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <label className="text-slate-500 inline-flex items-center gap-1.5 cursor-pointer">
                <input data-testid="round-off-toggle" type="checkbox" checked={roundOff} onChange={(e) => setRoundOff(e.target.checked)} className="w-3.5 h-3.5" />
                <span>Round to nearest ₹</span>
              </label>
              <span className={`font-mono-num ${totals.rOff < 0 ? "text-red-600" : totals.rOff > 0 ? "text-emerald-600" : "text-slate-400"}`}>{totals.rOff !== 0 ? (totals.rOff > 0 ? "+" : "") + totals.rOff.toFixed(2) : "—"}</span>
            </div>

            <div className="flex justify-between items-baseline pt-2 border-t border-slate-200">
              <span className="font-display font-bold text-sm">Total</span>
              <span className="font-display font-bold text-2xl font-mono-num text-slate-900" data-testid="pos-grand-total">{inr(totals.grand)}</span>
            </div>

            {/* Payments */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold inline-flex items-center gap-1"><Wallet size={11} /> Payment</div>
                {payments.length < 3 && <button onClick={addPayment} data-testid="add-payment" className="text-[10px] font-bold text-emerald-700 hover:underline inline-flex items-center gap-0.5"><Plus size={10} weight="bold" /> Split</button>}
              </div>
              {payments.map((p, i) => (
                <div key={i} className="mb-1.5" data-testid={`payment-row-${i}`}>
                  <div className="flex items-center gap-1.5">
                    <select value={p.method} onChange={(e) => updatePayment(i, "method", e.target.value)} className="h-8 px-2 border border-slate-300 rounded-md text-[11px] uppercase font-semibold">
                      {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
                    </select>
                    <input type="number" step="0.01" value={p.amount} onChange={(e) => updatePayment(i, "amount", e.target.value)} className="flex-1 h-8 px-2 border border-slate-300 rounded-md text-[11px] font-mono-num text-right" />
                    {(p.method === "card" || p.method === "upi") && (
                      <input type="text" value={p.reference} onChange={(e) => updatePayment(i, "reference", e.target.value)} placeholder="Ref" className="w-16 h-8 px-1.5 border border-slate-300 rounded-md text-[10px] font-mono-num" />
                    )}
                    {payments.length > 1 && <button onClick={() => removePayment(i)} className="p-1 text-red-600"><Trash size={12} /></button>}
                  </div>
                  {p.method === "credit_note" && (
                    <div className="mt-1">
                      <div className="flex items-center gap-1.5">
                        <input type="text" value={p.reference} onChange={(e) => updatePayment(i, "reference", e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), verifyCN(i))} placeholder="CN number e.g. CN/RNEXTN/2026/00001" data-testid={`cn-ref-${i}`} className="flex-1 h-8 px-2 border border-amber-300 bg-amber-50/40 rounded-md text-[10px] font-mono-num" />
                        <button onClick={() => verifyCN(i)} data-testid={`cn-verify-${i}`} className="h-8 px-2.5 bg-amber-700 hover:bg-amber-800 text-white text-[10px] font-bold rounded-md shrink-0">Verify</button>
                      </div>
                      {cnInfo[i] && (
                        <div className={`text-[10px] font-semibold mt-0.5 ${cnInfo[i].status === "ok" ? "text-emerald-700" : "text-red-600"}`} data-testid={`cn-info-${i}`}>
                          {cnInfo[i].status === "ok" ? "✓ " : "✕ "}{cnInfo[i].msg}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
              {payments.length > 1 && (
                <button onClick={autoBalanceLast} className="text-[10px] font-semibold text-slate-600 hover:text-slate-900">↩ Auto-balance last to {inr(totals.grand)}</button>
              )}
              {Math.abs(paymentDelta) > 0.05 && (
                <div className={`text-[11px] font-semibold mt-1 ${paymentDelta > 0 ? "text-red-600" : "text-amber-600"}`} data-testid="payment-mismatch">
                  {paymentDelta > 0 ? `Short by ${inr(paymentDelta)}` : `Over by ${inr(-paymentDelta)}`}
                </div>
              )}
            </div>

            {/* Notes / remarks */}
            <div className="pt-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-1">Notes / Remarks</div>
              <textarea
                data-testid="pos-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="e.g. Deliver next Sunday, advance received…"
                className="w-full px-2 py-1.5 border border-slate-300 rounded-md text-[11px] resize-none"
              />
            </div>
          </div>

          <button
            onClick={checkout}
            disabled={saving || lines.length === 0 || !executive || Math.abs(paymentDelta) > 0.05}
            data-testid="pos-checkout-btn"
            className="h-16 bg-emerald-500 hover:bg-emerald-600 text-white font-display font-bold text-lg disabled:opacity-50 transition-colors"
          >
            {saving ? "Processing…" : <span className="inline-flex items-center gap-2"><CurrencyInr size={20} weight="bold" /> Complete Checkout</span>}
          </button>
        </div>
      </div>
      {showApproval && (
        <ApprovalModal
          reason={approvalReason}
          onClose={() => setShowApproval(false)}
          onApproved={(d) => { setApproval(d); setShowApproval(false); }}
        />
      )}
    </Shell>
  );
}
